/**
 * Provider-agnostic cart math helpers shared by PayPal and Stripe code paths.
 *
 * Service-fee formula (calculateServiceFee) lives in lib/config.ts and is
 * already a single source of truth, so it stays there.
 *
 * The seller-level platform-fee composition
 *   `SELLING_FEE_RATE * sellerNet + sellerServiceFee`
 * is intentionally NOT extracted here. It is interleaved with the proportional
 * service-fee split and the PayPal `actualPlatformFees` reconciliation in
 * lib/order-creation.ts. Pulling it out without that surrounding context would
 * be a behavior change. The Stripe path duplicates the same formula at its
 * own call site, with lib/config.ts SELLING_FEE_RATE remaining the single
 * source of truth for the rate value.
 */

export interface ShippableItem {
  quantity: number;
  selectedShippingMethod?: {
    id: string;
    price?: number | null;
    extraValue?: number | null;
    allowCombinedShipping?: boolean;
  } | null;
}

/**
 * Total shipping cost for a single seller's slice of the cart.
 *
 * Two regimes per item, decided by the chosen shipping method:
 *  - non-combinable: `price × quantity` per item
 *  - combinable (grouped by shipping-method id):
 *      `base + (totalQty − 1) × extra`
 *      where `base = method.price` and `extra = method.extraValue`
 *
 * Items without a selected shipping method contribute 0.
 */
export function calculateSellerShippingTotal(items: ShippableItem[]): number {
  let total = 0;

  items
    .filter((i) => !i.selectedShippingMethod?.allowCombinedShipping)
    .forEach((i) => {
      total += (i.selectedShippingMethod?.price ?? 0) * i.quantity;
    });

  const combinable = items.filter((i) => i.selectedShippingMethod?.allowCombinedShipping);
  const groups: Record<string, ShippableItem[]> = {};
  combinable.forEach((i) => {
    const key = i.selectedShippingMethod!.id;
    (groups[key] ||= []).push(i);
  });
  Object.values(groups).forEach((group) => {
    const sm = group[0]?.selectedShippingMethod;
    if (!sm) return;
    const base = sm.price ?? 0;
    const extra = sm.extraValue ?? 0;
    const qty = group.reduce((sum, i) => sum + i.quantity, 0);
    total += base + (qty - 1) * extra;
  });

  return total;
}
